import { VideoList } from "../components/VideoSuggestions"
import { useVideoAPI } from "../services/api/hooks/useVideoAPI"
import { useSnackbarContext } from "../services/snackbar"
import { Box, Button, Grid, Tooltip, Typography, useTheme } from "@mui/material"
import React, { useEffect, useState } from "react"
import { Navigate, useNavigate, useLocation, useParams, useSearchParams, replace } from "react-router-dom"
import { VideoQueryType, VideoType } from "../services/api/video"
import { VideoPlayer } from "../components/VideoPlayer"
import { VideoControls } from "../components/VideoControls"
import { useAuthContext } from "../services/auth"
import { UserRole } from "../services/auth/auth.types"
import { StylelessLink } from "../components/Util"
import { VideoThumbnail, VideoThumbnailImage } from "../components/VideoThumbnail"
import { httpClient } from "../services/http"
import { useDirectAPI } from "../services/api/hooks/useDirectAPI"
import { DirectQueryType } from "../services/api/direct"
import { PlaylistQueryType, PlaylistType } from "../services/api/playlist"
import { usePlaylistAPI } from "../services/api/hooks/usePlaylistAPI"
import { useStaticContext } from "../services/static"

import { ReactComponent as CrossIcon } from "../assets/svg/cross.svg"
import { ReactComponent as LoopIcon } from "../assets/svg/loop.svg"
import { ReactComponent as BookmarkIcon } from "../assets/svg/bookmark.svg"
import { ReactComponent as FeatherIcon } from "../assets/svg/feather.svg"
import { ReactComponent as StarIcon } from "../assets/svg/star.svg"
import { ReactComponent as ShuffleIcon } from "../assets/svg/shuffle.svg"
import useWindowDimensions from "../services/useWindowDimensions"
import { VideoCarousel } from "../components/Carousels"

interface VideoProps {
  searchFocused?: boolean
}

export const OldURLVideoRedirect = (): React.ReactElement => {
  const { videoId: oldFormatVideoId } = useParams<{ videoId: string }>()
  return <Navigate to={`/video?v=${oldFormatVideoId}`} replace={true} />
}

export const Video = ({ searchFocused = false }: VideoProps): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams()
  const videoId = parseInt(searchParams.get("v") || "-1")
  const playlistCode = searchParams.get("pl") || null
  const navigate = useNavigate()

  const { open: sbOpen } = useSnackbarContext()
  const location = useLocation()
  const { role, isAuth, userId } = useAuthContext()
  const theme = useTheme()
  const { width } = useWindowDimensions()
  const { reloadPlaylists: reloadLibrary, playlists: library } = useStaticContext()

  const [video, setVideo] = useState<VideoType | undefined>()
  const [playlist, setPlaylist] = useState<PlaylistType | null>(null)
  const [playlistVideos, setPlaylistVideos] = useState<VideoType[]>([])
  const [videoIndex, setVideoIndex] = useState<number>(-1)
  const [resolution, setResolution] = useState<number>(1080)
  const [redirectLogin, setRedirectLogin] = useState<boolean>(false)
  const [loopPlaylist, setLoopPlaylist] = useState<boolean>(false)
  const [shufflePlaylist, setShufflePlaylist] = useState<boolean>(false)

  const { videosData, error, refresh } = useVideoAPI({
    constraint: VideoQueryType.VIDEO,
    id: videoId,
  })
  const { videosData: randomVideosData, refresh: refreshRandom } = useVideoAPI({
    constraint: VideoQueryType.RANDOM,
    id: videoId,
  })
  const { playlistsData, error: playlistError } = usePlaylistAPI({
    constraint: PlaylistQueryType.PLAYLIST,
    code: playlistCode ?? "",
  })
  const {
    videosData: playlistVideosData,
    loading: playlistVideosLoading,
    error: playlistVideosError,
  } = useVideoAPI({
    constraint: VideoQueryType.PLAYLIST,
    code: playlistCode ?? "",
  })

  useEffect(() => {
    refresh()
    refreshRandom()
    httpClient.instance.put(`/videos/history/${videoId}`)
  }, [videoId])

  useEffect(() => {
    if (videosData && videosData[0]) {
      setVideo(videosData[0])
    }
  }, [videosData])

  useEffect(() => {
    if (error) {
      if (error.status === 401 || error.status === 403) {
        if (!isAuth()) {
          setRedirectLogin(true)
          sbOpen("Veuillez vous connecter", "info")
          return
        } else {
          sbOpen("Cette vidéo n'est pas accessible", "error")
        }
      }
      if (error.status === 404) {
        sbOpen("Cette vidéo n'est pas accessible", "error")
        navigate("/")
        return
      }
      sbOpen("Une erreur s'est produite", "error")
      navigate("/")
    }
  }, [error])

  useEffect(() => {
    if (playlistCode && playlistsData && playlistsData[0]) {
      setPlaylist(playlistsData[0])
    }
  }, [playlistCode, playlistsData])

  useEffect(() => {
    if (!playlistVideosLoading && playlistVideosData) {
      setPlaylistVideos(playlistVideosData)
    }
  }, [playlistVideosData])

  useEffect(() => {
    if (!playlistVideosLoading && playlistVideos) {
      const index = playlistVideos.findIndex((vid) => vid.id === videoId)
      if (index === -1) {
        setSearchParams(
          (prev) => {
            prev.delete("pl")
            return prev
          },
          { replace: true },
        )
      } else {
        setVideoIndex(index)
      }
    }
  }, [videoId, playlistVideos])

  useEffect(() => {
    if (playlistError || playlistVideosError) {
      sbOpen("Erreur lors du chargement de la playlist", "error")
      setSearchParams(
        (prev) => {
          prev.delete("pl")
          return prev
        },
        { replace: true },
      )
    }
  }, [playlistError, playlistVideosError])

  const handleVideoEnd = (): void => {
    if (playlist && videoIndex !== -1) {
      if (videoIndex + 1 < playlistVideos.length) {
        const nextVideo = playlistVideos[videoIndex + 1]
        setSearchParams(
          (prev) => {
            prev.set("v", String(nextVideo.id))
            return prev
          },
          { replace: nextVideo.id === videoId },
        )
      } else if (loopPlaylist) {
        const nextVideo = playlistVideos[0]
        setSearchParams(
          (prev) => {
            prev.set("v", String(nextVideo.id))
            return prev
          },
          { replace: nextVideo.id === videoId },
        )
      }
    }
  }

  const saved = library.some((pl) => pl.code === playlist?.code)

  const handleSave = async () => {
    if (!playlist) return
    try {
      if (saved) {
        await httpClient.instance.delete(`/library/${playlist.code}`)
        sbOpen("Playlist supprimée de votre bibliothèque", "info")
      } else {
        await httpClient.instance.put(`/library/${playlist.code}`)
        sbOpen("Playlist ajoutée à votre bibliothèque", "success")
      }
    } catch (e) {
      sbOpen("Une erreur s'est produite", "error")
      console.error(e)
    } finally {
      reloadLibrary()
    }
  }

  const handleShuffle = (): void => {
    if (playlist) {
      const sortValues = new Map(playlistVideos.map((vid) => [vid.id, vid.id === videoId ? -1 : Math.random()]))
      const shuffledVideos = [...playlistVideos].sort((a, b) => {
        const sortA = sortValues.get(a.id) ?? 0
        const sortB = sortValues.get(b.id) ?? 0
        return sortA - sortB
      })
      setPlaylistVideos(shuffledVideos)
    }
  }

  const handleResetOrder = (): void => {
    if (playlistVideosData) {
      setPlaylistVideos(playlistVideosData)
    }
  }

  if (redirectLogin) {
    return <Navigate to="/login" state={{ from: location }} />
  }

  return (
    <>
      <Box display="flex" flexDirection="row" justifyContent="center" flexWrap="wrap">
        <Box display={"flex"} flexGrow={3} justifyContent={"center"} width={{ xs: "100%", lg: "calc(100% - 500px)" }}>
          <Box margin={{ xs: 0, sm: 2 }} maxWidth={theme.breakpoints.values.lg} width={"100%"}>
            <VideoPlayer
              video={video}
              resolution={resolution}
              disableKeyboardShortcuts={searchFocused}
              onVideoEnd={handleVideoEnd}
            />
            {video && <VideoControls video={video} resolution={resolution} setResolution={setResolution} />}
            {video && (role === UserRole.HYRIS || role === UserRole.GEEK) && (
              <Button
                sx={{ margin: 1 }}
                variant="outlined"
                color="secondary"
                onClick={() => navigate(`/editVideo/${video.id}`)}
              >
                {"Modifier"}
              </Button>
            )}
          </Box>
        </Box>
        {playlist && playlistCode && playlistVideos.length ? (
          <Box flexGrow={1} width={{ xs: "100%", lg: "400px" }} margin={2}>
            <Box border={`1px solid ${theme.palette.soft.main}`} borderRadius={4} overflow={"hidden"}>
              <Box
                sx={{
                  backgroundColor: theme.palette.element.playlist.header,
                  padding: 2,
                  pb: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                borderBottom={`1px solid ${theme.palette.soft.main}`}
              >
                <Box sx={{ overflow: "hidden", minWidth: 0 }}>
                  <Typography variant="h6" component="h3" textOverflow="ellipsis" noWrap>
                    <StylelessLink to={`/playlist?pl=${playlist.code}`}>{playlist.title}</StylelessLink>
                    {playlist.authorId === userId && (
                      <Box ml={1} component={"span"} display="inline">
                        <Tooltip title={"Créée par vous"}>
                          <FeatherIcon
                            width={"auto"}
                            height={"1rem"}
                            fill={theme.palette.mode === "dark" ? "white" : "black"}
                          />
                        </Tooltip>
                      </Box>
                    )}
                    {playlist.isPublic && (
                      <Box ml={1} component={"span"} display="inline">
                        <Tooltip title={"Featurée"}>
                          <StarIcon width={"auto"} height={"1rem"} />
                        </Tooltip>
                      </Box>
                    )}
                  </Typography>
                  <Typography color="grey">{`${playlistVideos.length} vidéo${
                    playlistVideos.length > 1 ? "s" : ""
                  }`}</Typography>
                  <LoopIcon
                    style={{
                      cursor: "pointer",
                      marginLeft: "10px",
                      backgroundColor: loopPlaylist ? theme.palette.secondary.main : "transparent",
                      borderRadius: "50%",
                      transition: "background-color 0.3s",
                      margin: "5px",
                      padding: "5px",
                    }}
                    width={"40px"}
                    height={"40px"}
                    stroke={theme.palette.mode === "dark" ? "white" : "black"}
                    onClick={() => setLoopPlaylist(!loopPlaylist)}
                  />
                  {playlistVideos.length > 2 && (
                    <ShuffleIcon
                      style={{
                        cursor: "pointer",
                        marginLeft: "10px",
                        backgroundColor: shufflePlaylist ? theme.palette.secondary.main : "transparent",
                        borderRadius: "50%",
                        transition: "background-color 0.3s",
                        margin: "5px",
                        padding: "5px",
                      }}
                      width={"40px"}
                      height={"40px"}
                      stroke={theme.palette.mode === "dark" ? "white" : "black"}
                      onClick={() => {
                        if (!shufflePlaylist) {
                          handleShuffle()
                        } else {
                          handleResetOrder()
                        }
                        setShufflePlaylist(!shufflePlaylist)
                      }}
                    />
                  )}
                  {playlist.authorId !== userId && (
                    <BookmarkIcon
                      style={{
                        cursor: "pointer",
                        marginLeft: "10px",
                        borderRadius: "50%",
                        transition: "background-color 0.3s, fill 0.3s",
                        margin: "5px",
                        padding: "5px",
                      }}
                      width={"40px"}
                      height={"40px"}
                      stroke={theme.palette.mode === "dark" ? "white" : "black"}
                      fill={saved ? (theme.palette.mode === "dark" ? "white" : "black") : "transparent"}
                      onClick={handleSave}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    ml: 2,
                    flex: "0 0 auto",
                    display: "flex",
                    alignItems: "center",
                    padding: "5px",
                    borderRadius: "50%",
                    "&:hover": { backgroundColor: theme.palette.soft.main },
                    transition: "background-color 0.2s",
                  }}
                >
                  <CrossIcon
                    width={"25px"}
                    height={"25px"}
                    fill={theme.palette.mode === "dark" ? "white" : "black"}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setPlaylist(null)
                      setSearchParams(
                        (prev) => {
                          prev.delete("pl")
                          return prev
                        },
                        { replace: true },
                      )
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ maxHeight: "70vh", overflowY: "auto" }}>
                {playlistVideos.map((vid) => (
                  <WatchListElement key={vid.id} video={vid} playlist={playlist} currentVideoId={video?.id} />
                ))}
              </Box>
            </Box>
          </Box>
        ) : null}
      </Box>
      {video && <SameDirectSuggestions video={video} />}
      {randomVideosData && randomVideosData.length ? (
        <Box borderTop="1px solid darkgray" sx={{ py: 5, px: 3 }}>
          <Typography component="h3" variant="h3" fontSize={"2rem"} mb={2}>
            <StylelessLink to="/videos">{"Vidéos au hasard"}</StylelessLink>
          </Typography>
          {width >= theme.breakpoints.values.lg ? (
            <VideoCarousel videos={randomVideosData.slice(0, 16)} />
          ) : (
            <Grid container spacing={1} direction="row" sx={{ py: 2 }}>
              {randomVideosData.slice(0, 12).map((video) => (
                <Grid key={video.id} size={{ xs: 12, sm: 4, md: 3 }}>
                  <VideoThumbnail video={video} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      ) : (
        <></>
      )}
    </>
  )
}

const WatchListElement = ({
  video,
  playlist,
  currentVideoId,
}: {
  video: VideoType
  playlist: PlaylistType
  currentVideoId?: number
}): React.ReactElement => {
  const navigate = useNavigate()
  const theme = useTheme()

  const handleClick = () => {
    navigate(`/video?v=${video.id}&pl=${playlist.code}`)
  }

  return (
    <Box
      onClick={handleClick}
      sx={{
        cursor: "pointer",
        display: "flex",
        padding: 1,
        bgcolor: video.id === currentVideoId ? theme.palette.element.playlist.current : theme.palette.primary.dark,
        maxHeight: "100px",
        "&:hover": {
          backgroundColor:
            video.id === currentVideoId ? theme.palette.element.playlist.current : theme.palette.primary.main,
        },
        transition: "background-color 0.2s",
      }}
    >
      <Box sx={{ width: "40%", maxWidth: "150px", marginRight: 1 }}>
        <VideoThumbnailImage video={video} adaptiveBorder={false} />
      </Box>
      <Box sx={{ width: "60%" }}>
        <Typography marginLeft={1} variant="h5" fontSize={"1.2rem"} overflow={"hidden"} textOverflow="ellipsis">
          {video.title}
        </Typography>
      </Box>
    </Box>
  )
}

export const SameDirectSuggestions = ({ video }: { video: VideoType }): React.ReactElement => {
  const directSuggestions = useVideoAPI({
    constraint: VideoQueryType.DIRECT,
    id: video.directId,
    exclude: String(video.id),
  })
  const direct = useDirectAPI({
    id: `${video.directId || -1}`,
    constraint: DirectQueryType.DIRECT,
  })

  useEffect(() => {
    directSuggestions.refresh()
  }, [video.id])

  return directSuggestions && directSuggestions.videosData.length ? (
    <Box sx={{ py: 2, px: 3 }}>
      <Typography component="h3" variant="h3" fontSize={"2rem"}>
        <StylelessLink to={`/direct?d=${video.directId}`}>{"Du même DT"}</StylelessLink>
      </Typography>
      {direct.directsData?.length ? (
        <Typography component="h3" variant="h3" fontSize={"1.5rem"} color={"grey"}>
          <StylelessLink to={`/direct?d=${video.directId}`}>{direct.directsData[0].title}</StylelessLink>
        </Typography>
      ) : (
        <></>
      )}
      <VideoList videos={directSuggestions.videosData} md={3} />
    </Box>
  ) : (
    <></>
  )
}
